import { Badge } from '@/components/ui/badge'
import type { MailingListFilters } from '@/features/mailing-list'
import type { MailingListSegment, MailingListSource } from '@/types'

const SEGMENTS: { value: MailingListSegment; label: string }[] = [
  { value: 'general', label: 'General' },
  { value: 'film-updates', label: 'Film Updates' },
  { value: 'services', label: 'Services' },
  { value: 'oddfest', label: 'ODDFEST' },
  { value: 'shift', label: 'S.H.I.F.T.' },
]

const SOURCES: { value: MailingListSource; label: string }[] = [
  { value: 'footer-signup', label: 'Footer Signup' },
  { value: 'service-request', label: 'Service Request' },
  { value: 'admin-manual', label: 'Admin (Manual)' },
  { value: 'admin-bulk', label: 'Admin (Bulk)' },
  { value: 'admin-csv', label: 'Admin (CSV)' },
]

interface Props {
  filters: MailingListFilters
  onChange: (filters: MailingListFilters) => void
  totalCount: number
  filteredCount: number
}

export function MailingListFiltersBar({ filters, onChange, totalCount, filteredCount }: Props) {
  function toggleSegment(value: MailingListSegment) {
    const next = filters.segments.includes(value)
      ? filters.segments.filter((s) => s !== value)
      : [...filters.segments, value]
    onChange({ ...filters, segments: next })
  }

  function toggleSource(value: MailingListSource) {
    const next = filters.sources.includes(value)
      ? filters.sources.filter((s) => s !== value)
      : [...filters.sources, value]
    onChange({ ...filters, sources: next as MailingListSource[] })
  }

  function clearFilters() {
    onChange({ segments: [], sources: [], subscribedOnly: false })
  }

  const hasFilters =
    filters.segments.length > 0 || filters.sources.length > 0 || filters.subscribedOnly

  return (
    <div className="space-y-3 rounded-md border p-4">
      <div className="flex flex-wrap items-center gap-4">
        {/* Segment filters */}
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">Segments:</span>
          {SEGMENTS.map(({ value, label }) => {
            const active = filters.segments.includes(value)
            return (
              <button
                key={value}
                type="button"
                onClick={() => toggleSegment(value)}
                className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Badge
                  variant={active ? 'default' : 'outline'}
                  className="cursor-pointer select-none"
                >
                  {label}
                </Badge>
              </button>
            )
          })}
        </div>

        {/* Source filters */}
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="text-xs font-medium text-muted-foreground">Source:</span>
          {SOURCES.map(({ value, label }) => {
            const active = filters.sources.includes(value)
            return (
              <button
                key={value}
                type="button"
                onClick={() => toggleSource(value)}
                className="rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                <Badge
                  variant={active ? 'default' : 'outline'}
                  className="cursor-pointer select-none"
                >
                  {label}
                </Badge>
              </button>
            )
          })}
        </div>

        {/* Subscribed only toggle */}
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={filters.subscribedOnly}
            onChange={(e) => onChange({ ...filters, subscribedOnly: e.target.checked })}
            className="h-4 w-4 rounded border-border accent-primary"
          />
          Subscribed only
        </label>
      </div>

      <div className="flex items-center justify-between">
        <p className="text-xs text-muted-foreground">
          {hasFilters
            ? `Showing ${filteredCount} of ${totalCount} members`
            : `${totalCount} members total`}
        </p>
        {hasFilters && (
          <button
            type="button"
            onClick={clearFilters}
            className="text-xs text-muted-foreground underline hover:text-foreground"
          >
            Clear filters
          </button>
        )}
      </div>
    </div>
  )
}
